import axios, { AxiosPromise, AxiosResponse } from 'axios';
import { dispatch } from '../store/Index';
import { generalShowErrorSnackbar } from '../store/general/Functions';
import { authSetPPECBToken, authSetTURToken } from '../store/auth/Actions';
import { IConsignmentTokenResponse } from '../@types/model/compliance/titan-TUR/consignmentTokenResponse';
import { ITURTokenResponse } from '../@types/model/compliance/titan-TUR/turTokenResponse';
import { IConsignmentResponse } from '../@types/model/compliance/titan-TUR/consignmentResponse';
import { IConsignment } from '../@types/model/compliance/titan-TUR/consignment';
import { IConsignmentPostResponse } from '../@types/model/compliance/titan-TUR/consignmentPostResponse';
import { IELot } from '../@types/model/compliance/titan-TUR/eLot';
import { IELotResponse } from '../@types/model/compliance/titan-TUR/eLotResponse';
import { ITradeUnitResponse } from '../@types/model/compliance/titan-TUR/tradeUnitResponse';
import { IConsignmentPPECBResponse } from '../@types/model/compliance/titan-TUR/consignmentPPECBResponse';

export const getConsignmentToken = () : AxiosPromise<IConsignmentTokenResponse> => axios
    .get(`${API_URL}/v1/Consignment/GetPPECBToken`)
    .then((res : AxiosResponse<IConsignmentTokenResponse>) => {
        dispatch(authSetPPECBToken(res.data));
        return res.data;
    })
    .catch((err) => { generalShowErrorSnackbar('An error occurred getting a consignment token.'); return err; });

export const getTUMToken = async (organizationId : number) : Promise<ITURTokenResponse | null> => {
    try {
        const result = await axios.get(`${API_URL}/v1/Consignment/TUMToken`, {
            params: {
                organizationId,
            },
        });

        dispatch(authSetTURToken(result.data));
        return result.data;
    } catch (ex) {
        generalShowErrorSnackbar('An error occurred getting a TUR token.');
        return null;
    }
};

export const getConsignmentResult = (consignmentNumber : string) : AxiosPromise<IConsignmentResponse> => {
    return axios.get(`${API_URL}/v1/Consignment/GetConsignmentPPECBResult?consignmentNumber=${consignmentNumber}`)
        .then(res => res)
        .catch((err) => {
            generalShowErrorSnackbar('An error occurred retrieving the consignment.');
            return err;
        });
};

export const uploadConsignmentOld = (consignment : IConsignment) : AxiosPromise<IConsignmentPPECBResponse> => {
    return axios.post(`${API_URL}/v1/Consignment/UploadConsignmentToPPECB`, consignment)
        .then(res => res)
        .catch(err => err);
};

export const uploadConsignment = (complianceId : number) : AxiosPromise<IConsignmentPPECBResponse> => {
    return axios.post(`${API_URL}/v2/Consignment/UploadConsignmentToPPECB`, { complianceId })
        .then(res => res)
        .catch(err => err);
};

export const updateConsignment = (consignment : IConsignment) : AxiosPromise<IConsignmentPostResponse> => {
    return axios.post(`${API_URL}/v1/Consignment/UpdatePPECBConsignment`, consignment)
        .then(res => res)
        .catch(err => err);
};

export const deleteConsignment = (inspectionMessageId : number) : AxiosPromise<IConsignmentPostResponse> => {
    return axios.delete(`${API_URL}/v1/Consignment/DeletePPECBConsignment`, {
        params: { inspectionMessageId },
    })
        .then(res => res)
        .catch((err) => { generalShowErrorSnackbar('An error occurred validating the consignment.'); return err; });
};

export const getAgreementCode = (commodityCode : string, countryCode : string) : AxiosPromise<string> => {
    return axios.get(`${API_URL}/v1/Consignment/AgreementCode?commodityCode=${commodityCode}&countryCode=${countryCode}`)
        .then(res => res)
        .catch((err) => { generalShowErrorSnackbar('An error occurred getting the agreement code.'); return err; });
};

export const upsertELot = (data : IELot) : AxiosPromise<IELotResponse> => {
    return axios.post(`${API_URL}/v1/Consignment/PostELot`, data)
        .then(res => res)
        .catch((err) => { generalShowErrorSnackbar('An error occurred posting the ELot.'); return err; });
};

export const upsertTUMELot = (data : { complianceId : number; agreementCodeOverride ?: string | number}) : AxiosPromise<IELotResponse> => {
    return axios.post(`${API_URL}/v1/Consignment/PostTUMELot`, data)
        .then(res => res)
        .catch((err) => { generalShowErrorSnackbar('An error occurred posting the ELot.'); return err; });
};

export const createTUMV3TradeUnit = (data : { complianceId : number; agreementCodeOverride ?: string | number}) : AxiosPromise<ITradeUnitResponse> => {
    return axios.post(`${API_URL}/v1/Consignment/CreateTUMV3TradeUnit`, data)
        .then(res => res)
        .catch((err) => { generalShowErrorSnackbar('An error occurred creating the trade unit.'); return err; });
};

export const updateTUMV3TradeUnit = (data : { complianceId : number; agreementCodeOverride ?: string | number}) : AxiosPromise<ITradeUnitResponse> => {
    return axios.post(`${API_URL}/v1/Consignment/UpdateTUMV3TradeUnit`, data)
        .then(res => res)
        .catch((err) => { generalShowErrorSnackbar('An error occurred updating the trade unit.'); return err; });
};

export const deleteTUMV3TradeUnit = (data : { complianceId : number; agreementCodeOverride ?: string | number}) : AxiosPromise<ITradeUnitResponse> => {
    return axios.post(`${API_URL}/v1/Consignment/DeleteTUMV3TradeUnit`, data)
        .then(res => res)
        .catch((err) => { generalShowErrorSnackbar('An error occurred deleting the trade unit.'); return err; });
};

export const generateTUMV3Json = (complianceId : number) : AxiosPromise<string> => {
    return axios.post(`${API_URL}/v1/Consignment/GenerateTUMV3Json`, { complianceId })
        .then(res => res)
        .catch((err) => { generalShowErrorSnackbar('An error occurred generating the Json.'); return err; });
};
